/**
 * Currency formatting utilities for Nigerian Naira (₦)
 */

/**
 * Format a number as Nigerian Naira currency
 * @param amount - The amount to format (can be number or string)
 * @param options - Optional formatting options
 * @returns Formatted currency string (e.g., "₦1,234,567.89")
 */
export const formatCurrency = (
  amount: number | string | null | undefined,
  options: {
    showSymbol?: boolean;
    decimals?: number;
    useGrouping?: boolean;
  } = {}
): string => {
  const {
    showSymbol = true,
    decimals = 2,
    useGrouping = true,
  } = options;

  // Handle null, undefined, or empty string
  if (amount === null || amount === undefined || amount === '') {
    return showSymbol ? '₦0.00' : '0.00';
  }

  // Convert to number
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Handle NaN
  if (isNaN(numAmount)) {
    return showSymbol ? '₦0.00' : '0.00';
  }

  // Format the number
  const formatted = new Intl.NumberFormat('en-NG', {
    style: 'decimal',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping,
  }).format(numAmount);

  return showSymbol ? `₦${formatted}` : formatted;
};

/**
 * Parse a formatted currency string back to a number
 * @param formattedAmount - The formatted string (e.g., "₦1,234.56" or "1,234.56")
 * @returns The numeric value
 */
export const parseCurrency = (formattedAmount: string): number => {
  if (!formattedAmount) return 0;

  // Remove currency symbol, spaces, and commas
  const cleaned = formattedAmount.replace(/[₦,\s]/g, '');
  const parsed = parseFloat(cleaned);

  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Format currency input as user types (adds commas, handles decimals)
 * @param value - The input value
 * @param previousValue - The previous input value (for backspace handling)
 * @returns Formatted input value
 */
export const formatCurrencyInput = (
  value: string,
  previousValue: string = ''
): string => {
  // Remove all non-numeric characters except decimal point
  let cleaned = value.replace(/[^\d.]/g, '');

  // Ensure only one decimal point
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    cleaned = parts[0] + '.' + parts.slice(1).join('');
  }

  // Limit to 2 decimal places
  if (parts.length === 2) {
    cleaned = parts[0] + '.' + parts[1].substring(0, 2);
  }

  // If empty, return empty string
  if (!cleaned) return '';

  // Split into integer and decimal parts
  const [integerPart, decimalPart] = cleaned.split('.');

  // Add thousand separators to integer part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Reconstruct with decimal if present
  return decimalPart !== undefined
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;
};

/**
 * Validate if a currency amount is valid
 * @param amount - The amount to validate
 * @param min - Minimum allowed value
 * @param max - Maximum allowed value
 * @returns Validation result with error message if invalid
 */
export const validateCurrencyAmount = (
  amount: number | string,
  min?: number,
  max?: number
): { valid: boolean; error?: string } => {
  const numAmount = typeof amount === 'string' ? parseCurrency(amount) : amount;

  if (isNaN(numAmount)) {
    return { valid: false, error: 'Please enter a valid amount' };
  }

  if (numAmount <= 0) {
    return { valid: false, error: 'Amount must be greater than zero' };
  }

  if (min !== undefined && numAmount < min) {
    return {
      valid: false,
      error: `Amount must be at least ${formatCurrency(min)}`,
    };
  }

  if (max !== undefined && numAmount > max) {
    return {
      valid: false,
      error: `Amount cannot exceed ${formatCurrency(max)}`,
    };
  }

  return { valid: true };
};
